import { Card, Flex, Text, Badge, Spinner } from '@radix-ui/themes'
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons'
import LinkButton from '~/components/LinkButton'
import { RichCalloutCard, RichCalloutCardProps } from '~/components/RichCalloutCard'
import { LuCircleCheck, LuTriangleAlert } from 'react-icons/lu'
import { useQuery } from '@tanstack/react-query'
import { IncidentType, listSiteIncidents, SYSTEM_INCIDENT_TYPES } from '../service'
import { Tables } from '@willie/supabase'
import { useTranslation } from 'react-i18next'
import { useFeatureEnabled } from '~/features/internal-analytics/useFeatureEnabled'

type Incident = Tables<'incidents'>

type IncidentsStatusSummaryProps = {
  siteID: string
  displayAsHeader?: boolean
  variant?: RichCalloutCardProps['variant']
}

export const IncidentsStatusSummary = ({
  siteID,
  displayAsHeader,
  variant = 'surface'
}: IncidentsStatusSummaryProps) => {
  const { t } = useTranslation('sites')
  const { data: incidents = [], isLoading } = useQuery({
    queryKey: ['siteIncidents', siteID],
    queryFn: () => listSiteIncidents(siteID, { status: 'active' })
  })

  const totalIncidents = incidents.length
  const hasActiveIncidents = totalIncidents > 0

  const incidentsByType: Record<IncidentType, Incident[]> = SYSTEM_INCIDENT_TYPES.reduce(
    (acc, type) => ({
      ...acc,
      [type]: incidents.filter((incident) => incident.type === type)
    }),
    {} as Record<IncidentType, Incident[]>
  )

  if (!useFeatureEnabled('incidents-status')) {
    return null
  }

  if (isLoading) {
    return (
      <Card>
        <Flex align="center" justify="center" p="4">
          <Spinner size="3" />
        </Flex>
      </Card>
    )
  }

  return (
    <RichCalloutCard
      variant={variant}
      color={hasActiveIncidents ? 'amber' : 'green'}
      title={hasActiveIncidents ? t('incidents.status.activeIncidents') : t('incidents.status.noActiveIncidents')}
      subtitle={t('incidents.status.incidentsAffectingSite', { count: totalIncidents })}
      icon={
        hasActiveIncidents ? (
          <LuTriangleAlert size={displayAsHeader ? 40 : 35} color={`var(--amber-11)`} />
        ) : (
          <LuCircleCheck size={displayAsHeader ? 40 : 35} color={`var(--green-9)`} />
        )
      }
      actions={
        displayAsHeader ? (
          <LinkButton
            to="/sites/$siteID/dashboard"
            params={{ siteID }}
            text={t('incidents.status.backToDashboard')}
            leadingIcon={<ArrowLeftIcon />}
          />
        ) : (
          <LinkButton
            color="gray"
            to="/sites/$siteID/incidents"
            params={{ siteID }}
            text={t('incidents.status.viewDetails')}
            trailingIcon={<ArrowRightIcon />}
          />
        )
      }
    >
      {!displayAsHeader && (
        <Flex direction="column" gap="5" p={'5'}>
          {Object.entries(incidentsByType).map(([type, incidents]) => (
            <Flex key={type} align="center" justify="between">
              <Flex align="center" gap="2">
                {incidents.length > 0 ? (
                  <LuTriangleAlert size={'18'} color={`var(--amber-11)`} />
                ) : (
                  <LuCircleCheck size={'18'} color={`var(--green-11)`} />
                )}
                <Text size="2" weight="medium">
                  {t(`incidents.status.types.${type}`)}
                </Text>
              </Flex>
              <Flex align="center" gap="2">
                <Badge color={incidents.length > 0 ? 'amber' : 'green'} variant="soft" size="1">
                  {t('incidents.status.activeIncidentsCount', { count: incidents.length })}
                </Badge>
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </RichCalloutCard>
  )
}
